import { useMemo, useState } from 'react';
import type {
  AccountUser,
  AccountUserLicenseTypeInput,
  AccountUserLicenseTypeValue,
  CreateMembershipsInput,
  CreateMembershipsMutation,
  MembershipMemberInput,
  MembershipResourceInput,
} from '@frameio/graphql-client/generated';
import { useCreateMembershipsMutation } from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import { useAccountManagedByAdobe } from '@frameio/shared/hooks';
import { toSupportedLocaleOrDefault } from '@frameio/shared/utils/toSupportedLocale';
import { type InputToken, InputTokenStatus } from '../../../utils';
import type { SelectedMember } from '../../../utils/SelectedMemberType';
import { STATUS_TOOLTIP } from '../../MemberAccessForm/memberAccessFormConstants';
import { convertUserOrGroupToInputToken } from '../../MemberAccessForm/utils';

type ExistingUser = Pick<
  AccountUser,
  'id' | 'name' | 'email' | 'profileImage' | 'avatarColor' | 'licenseType'
>;

export type UseMemberInputTokenArgs = {
  roleId: string;
  licenseType: AccountUserLicenseTypeInput;
  resource: MembershipResourceInput;
};

export const useMemberInputToken = ({
  roleId,
  resource,
  licenseType,
}: UseMemberInputTokenArgs) => {
  const [tokens, setTokens] = useState<InputToken[]>([]);
  const [members, setMembers] = useState<MembershipMemberInput[]>([]);
  const isManagedByAdobe = useAccountManagedByAdobe();

  const tokenIds = new Set(tokens.map((token) => token.id));

  const createMembershipsDryRun = useCreateMembershipsDryRun({
    roleId,
    resource,
    licenseType,
  });

  const addEmailsToTokens = async (emails: string[]) => {
    const data = await createMembershipsDryRun({
      roleId,
      members: emails.map((email) => ({ email })),
    });

    const newTokens: InputToken[] = [];
    const newMembers: MembershipMemberInput[] = [];

    data?.data?.createMemberships?.metadata?.userChanges?.forEach(
      (userChange) => {
        if (shouldSkipUserChange(userChange, tokenIds)) {
          return;
        }

        const { token, member } = processUserChange(
          userChange,
          licenseType,
          isManagedByAdobe
        );
        if (token) newTokens.push(token);
        if (member) newMembers.push(member);
      }
    );

    if (newTokens.length) {
      setTokens((prev) => [...prev, ...newTokens]);
    }

    if (newMembers.length) {
      setMembers((prev) => [...prev, ...newMembers]);
    }
  };

  const removeToken = (id: string) => {
    setTokens((prev) => prev.filter((token) => token.id !== id));
    setMembers((prev) =>
      prev.filter((member) => {
        if (member.email) {
          return member.email !== id;
        }
        if (member.accountUserId) {
          return member.accountUserId !== id;
        }

        if (member.accountUserGroupId) {
          return member.accountUserGroupId !== id;
        }

        return true;
      })
    );
  };

  const addSuggestionsToTokens = async (
    userOrGroups: Array<SelectedMember>
  ) => {
    const { tokensToAdd, membersToAdd } = userOrGroups.reduce(
      (acc, userOrGroup) => {
        if (!tokenIds.has(userOrGroup.id)) {
          acc.tokensToAdd.push(
            convertUserOrGroupToInputToken(
              userOrGroup,
              licenseType,
              isManagedByAdobe
            )
          );

          acc.membersToAdd.push({
            accountUserId:
              userOrGroup.__typename === 'SuggestedAccountUser'
                ? userOrGroup.id
                : undefined,
            accountUserGroupId:
              userOrGroup.__typename === 'SuggestedAccountUserGroup'
                ? userOrGroup.id
                : undefined,
          });
        }
        return acc;
      },
      {
        tokensToAdd: [] as InputToken[],
        membersToAdd: [] as Array<MembershipMemberInput>,
      }
    );

    setTokens((prev) => [...prev, ...tokensToAdd]);
    setMembers((prev) => [...prev, ...membersToAdd]);
  };

  const stats = useMemo(() => calculateTokenStats(tokens), [tokens]);

  const addInvalidEmails = (invalidEmails: string[]) => {
    const tokensToAdd = invalidEmails
      .filter((email) => !tokenIds.has(email))
      .map((email) => ({
        id: email,
        label: email,
        status: InputTokenStatus.INVALID_EMAIL,
        tooltip: STATUS_TOOLTIP[InputTokenStatus.INVALID_EMAIL]?.(email),
      }));

    setTokens((prev) => [...prev, ...tokensToAdd]);
  };

  const addNewMemberEmails = (emails: string[]) => {
    const tokensToAdd = emails
      .filter((email) => !tokenIds.has(email))
      .map((email) => ({
        id: email,
        label: email,
        status: InputTokenStatus.NEW_MEMBER,
        tooltip: STATUS_TOOLTIP[InputTokenStatus.NEW_MEMBER]?.(email),
      }));

    setTokens((prev) => [...prev, ...tokensToAdd]);
    setMembers((prev) => [...prev, ...emails.map((email) => ({ email }))]);
  };

  const resetTokens = () => {
    setTokens([]);
    setMembers([]);
  };

  return {
    inputMembers: members,
    inputTokens: tokens,
    inputTokenIds: tokenIds,
    resetTokens,
    removeToken,
    addEmailsToTokens,
    addSuggestionsToTokens,
    addInvalidEmails,
    addNewMemberEmails,
    ...stats,
  };
};

const useCreateMembershipsDryRun = ({
  roleId,
  resource,
  licenseType,
}: {
  roleId: string;
  resource: MembershipResourceInput;
  licenseType: AccountUserLicenseTypeInput;
}) => {
  const [createMembershipsMutation] = useCreateMembershipsMutation({
    errorPolicy: 'ignore',
  });

  return ({ members }: Pick<CreateMembershipsInput, 'roleId' | 'members'>) => {
    return createMembershipsMutation({
      variables: {
        locale: toSupportedLocaleOrDefault(intl().locale),
        input: {
          dryRun: true,
          members,
          roleId,
          licenseType,
          resource,
        },
      },
    });
  };
};

const getAccountUserAddedToken = (
  licenseType: AccountUserLicenseTypeValue,
  email: string,
  isManagedByAdobe: boolean
): InputToken => {
  const label = email;
  const id = email;

  if (isManagedByAdobe) {
    return {
      id,
      label,
      status: InputTokenStatus.NEW_MEMBER_WARN,
      tooltip: STATUS_TOOLTIP[InputTokenStatus.NEW_MEMBER_WARN]?.(),
    };
  }

  if (licenseType === 'GUEST') {
    return {
      id,
      label,
      status: InputTokenStatus.NEW_GUEST,
      tooltip: STATUS_TOOLTIP[InputTokenStatus.NEW_GUEST]?.(label),
    };
  }

  return {
    id,
    label,
    status: InputTokenStatus.NEW_MEMBER,
    tooltip: STATUS_TOOLTIP[InputTokenStatus.NEW_MEMBER]?.(label),
  };
};

const getAccountUserModifiedToken = (
  prevLicenseType: AccountUserLicenseTypeValue | undefined,
  newLicenseType: AccountUserLicenseTypeValue | undefined,
  user: ExistingUser
): InputToken | undefined => {
  let status = InputTokenStatus.MEMBER;
  const downgradingToGuest =
    prevLicenseType === 'MEMBER' && newLicenseType === 'GUEST';
  const upgradingToMember =
    (prevLicenseType === 'GUEST' || prevLicenseType === 'REVIEWER') &&
    newLicenseType === 'MEMBER';

  if (downgradingToGuest) {
    status = InputTokenStatus.EXISTING_MEMBER;
  } else if (upgradingToMember) {
    status = InputTokenStatus.NEW_MEMBER;
  }

  const label = (user.name || user.email) ?? '';

  return {
    id: user.id ?? '',
    label,
    avatarBackground: user.avatarColor ?? undefined,
    avatarSrc: user.profileImage?.image64 ?? undefined,
    status,
    tooltip: STATUS_TOOLTIP[status]?.(label),
  } satisfies InputToken;
};

const getAccountUserNoopToken = (
  licenseType: AccountUserLicenseTypeValue,
  user: Pick<
    AccountUser,
    'id' | 'name' | 'email' | 'profileImage' | 'avatarColor' | 'licenseType'
  >
) => {
  let status = InputTokenStatus.MEMBER;
  if (licenseType === 'GUEST' && user.licenseType === 'GUEST') {
    status = InputTokenStatus.EXISTING_GUEST;
  }

  const label = (user.name || user.email) ?? '';

  return {
    id: user.id ?? '',
    label,
    avatarBackground: user.avatarColor ?? undefined,
    avatarSrc: user.profileImage?.image64 ?? undefined,
    status,
    tooltip: STATUS_TOOLTIP[status]?.(label),
  } satisfies InputToken;
};

type UserChange = NonNullable<
  NonNullable<
    NonNullable<CreateMembershipsMutation['createMemberships']>['metadata']
  >['userChanges']
>[number];

const processUserChange = (
  userChange: UserChange,
  licenseType: AccountUserLicenseTypeValue,
  isManagedByAdobe: boolean
):
  | { token: InputToken; member: MembershipMemberInput }
  | { token: null; member: null } => {
  switch (userChange.__typename) {
    case 'AccountUserAdded':
      if (userChange.email) {
        return {
          token: getAccountUserAddedToken(
            licenseType,
            userChange.email,
            isManagedByAdobe
          ),
          member: { email: userChange.email },
        };
      }
      break;

    case 'AccountUserModified': {
      const modifiedToken = getAccountUserModifiedToken(
        userChange.previousAttributes?.licenseType ?? undefined,
        userChange.newAttributes?.licenseType ?? undefined,
        userChange.accountUser || ({} as AccountUser)
      );
      if (modifiedToken) {
        return {
          token: modifiedToken,
          member: { accountUserId: userChange.accountUser?.id },
        };
      }
      break;
    }

    case 'AccountUserNoop': {
      const noopToken = getAccountUserNoopToken(
        licenseType,
        userChange.accountUser || ({} as AccountUser)
      );
      if (noopToken) {
        return {
          token: noopToken,
          member: { accountUserId: userChange.accountUser?.id },
        };
      }
      break;
    }
    default:
      break;
  }

  return { token: null, member: null };
};

const calculateTokenStats = (tokens: InputToken[]) => {
  let firstExistingGuestToken: InputToken | undefined;
  let totalExistingGuests = 0;
  let hasInvalidEmails = false;
  let totalNewGuests = 0;
  let totalNewMembers = 0;

  tokens.forEach((token) => {
    if (!hasInvalidEmails && token.status === InputTokenStatus.INVALID_EMAIL) {
      hasInvalidEmails = true;
    }

    if (token.status === InputTokenStatus.EXISTING_GUEST) {
      totalExistingGuests += 1;
      if (!firstExistingGuestToken) {
        firstExistingGuestToken = token;
      }
    }

    if (token.status === InputTokenStatus.NEW_GUEST) {
      totalNewGuests += 1;
    }

    if (
      token.status === InputTokenStatus.NEW_MEMBER ||
      token.status === InputTokenStatus.NEW_MEMBER_WARN
    ) {
      totalNewMembers += 1;
    }
  });

  return {
    hasInvalidEmails,
    totalNewMembers,
    hasNewMembers: totalNewMembers > 0,
    totalExistingGuests,
    totalNewGuests,
    firstExistingGuestToken,
  };
};

const shouldSkipUserChange = (
  userChange: UserChange,
  tokenIds: Set<string>
): boolean => {
  if (userChange.__typename === 'AccountUserAdded') {
    return userChange.email ? tokenIds.has(userChange.email) : false;
  }
  return userChange.accountUser?.id
    ? tokenIds.has(userChange.accountUser.id)
    : false;
};
